import { z } from 'zod';
import { CALLOUT_VARIANTS } from './courses';
import { questionInputSchema, questionTypeSchema, skillSchema } from './quizzes';

/*
 * AI assist: drafts of course outlines, lessons and questions, generated by
 * Claude on Amazon Bedrock. Generation runs as an async job; the result is a
 * draft that the author reviews and edits before anything is saved.
 */

export const AI_JOB_KINDS = ['course_outline', 'lesson', 'questions'] as const;
export type AiJobKind = (typeof AI_JOB_KINDS)[number];

export const AI_JOB_STATUSES = ['queued', 'running', 'succeeded', 'failed'] as const;
export type AiJobStatus = (typeof AI_JOB_STATUSES)[number];

/** Pasted notes, transcripts or an existing course's text to work from. */
const sourceText = z.string().trim().max(60_000).optional();
/** Course files (PDF, Word, Excel, Markdown, text, HTML) to work from. */
const sourceAssetIds = z.array(z.string().uuid()).max(3).default([]);
/** Bedrock reads documents up to 4.5 MB. */
export const AI_SOURCE_MAX_BYTES = 4.5 * 1024 * 1024;
/** File extensions Bedrock can read as documents. */
export const AI_SOURCE_FORMATS = ['pdf', 'csv', 'doc', 'docx', 'xls', 'xlsx', 'html', 'txt', 'md'];

export const outlineJobInputSchema = z.object({
  kind: z.literal('course_outline'),
  courseId: z.string().uuid(),
  /** Topic, audience and goals. */
  brief: z.string().trim().min(10).max(4000),
  modules: z.number().int().min(1).max(12).default(4),
  lessonsPerModule: z.number().int().min(1).max(10).default(3),
  sourceText,
  sourceAssetIds,
});

export const lessonJobInputSchema = z.object({
  kind: z.literal('lesson'),
  lessonId: z.string().uuid(),
  instructions: z.string().trim().max(4000).optional(),
  minutes: z.number().int().min(3).max(120).default(15),
  sourceText,
  sourceAssetIds,
});

export const questionsJobInputSchema = z.object({
  kind: z.literal('questions'),
  /** Who the questions are for (decides who may run the job); null is Nirvik's library. */
  ownerOrgId: z.string().uuid().nullable(),
  topic: z.string().trim().max(4000).optional(),
  /** Write questions about this lesson's text (e.g. for a knowledge check). */
  lessonId: z.string().uuid().optional(),
  count: z.number().int().min(1).max(20).default(5),
  types: z.array(questionTypeSchema).min(1).max(3).default(['single']),
  /** 1 easy, 2 medium, 3 hard; omitted means a mix. */
  difficulty: z.number().int().min(1).max(3).optional(),
  /** Tag every question with this skill; otherwise the model picks short slugs. */
  skill: skillSchema.optional(),
  sourceText,
  sourceAssetIds,
});

export const aiJobInputSchema = z
  .discriminatedUnion('kind', [
    outlineJobInputSchema,
    lessonJobInputSchema,
    questionsJobInputSchema,
  ])
  .superRefine((input, ctx) => {
    if (
      input.kind === 'questions' &&
      !input.topic &&
      !input.lessonId &&
      !input.sourceText &&
      !input.sourceAssetIds.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['topic'],
        message: 'Give a topic, a lesson or source material',
      });
    }
  });
export type AiJobInput = z.infer<typeof aiJobInputSchema>;
export type AiJobRequest = z.input<typeof aiJobInputSchema>;

// ─── Drafts (job results) ────────────────────────────────────────────────

export const aiOutlineSchema = z.object({
  summary: z.string().trim().max(500),
  description: z.string().trim().max(5000),
  outcomes: z.array(z.string().trim().min(1).max(300)).max(10),
  modules: z
    .array(
      z.object({
        title: z.string().trim().min(1).max(200),
        summary: z.string().trim().max(1000).optional(),
        lessons: z
          .array(
            z.object({
              title: z.string().trim().min(1).max(200),
              summary: z.string().trim().max(1000).optional(),
              estimatedMinutes: z.number().int().min(1).max(600).optional(),
            }),
          )
          .min(1)
          .max(20),
      }),
    )
    .min(1)
    .max(20),
});
export type AiOutline = z.infer<typeof aiOutlineSchema>;

/** Generated lessons use text blocks only; authors add media themselves. */
export const aiLessonBlockSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('markdown'), markdown: z.string().min(1).max(100_000) }),
  z.object({
    type: z.literal('callout'),
    variant: z.enum(CALLOUT_VARIANTS).default('info'),
    title: z.string().trim().max(200).optional(),
    markdown: z.string().min(1).max(20_000),
  }),
]);
export const aiLessonSchema = z.object({
  summary: z.string().trim().max(1000),
  estimatedMinutes: z.number().int().min(1).max(600),
  blocks: z.array(aiLessonBlockSchema).min(1).max(30),
});
export type AiLesson = z.infer<typeof aiLessonSchema>;

export const aiQuestionsSchema = z.object({
  questions: z.array(questionInputSchema).min(1).max(20),
});
export type AiQuestions = z.infer<typeof aiQuestionsSchema>;

export type AiDraft =
  | { kind: 'course_outline'; outline: AiOutline }
  | { kind: 'lesson'; lesson: AiLesson }
  | { kind: 'questions'; questions: AiQuestions['questions'] };

export interface AiJob {
  id: string;
  kind: AiJobKind;
  status: AiJobStatus;
  result: AiDraft | null;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

/** Applying an outline draft: modules and empty lessons appended to a course. */
export const applyOutlineSchema = z.object({
  modules: aiOutlineSchema.shape.modules,
  /** Also replace the course's summary, description and outcomes. */
  details: aiOutlineSchema.pick({ summary: true, description: true, outcomes: true }).optional(),
});
export type ApplyOutline = z.infer<typeof applyOutlineSchema>;

/** Saving reviewed questions into a bank in one go. */
export const bulkQuestionsSchema = z.object({
  questions: z.array(questionInputSchema).min(1).max(50),
});
